import React from 'react'
import { createRoot } from 'react-dom/client'
import type { AppDispatch } from './store'
import type { ClientConfigRuntime } from './utils/client-config-runtime'
import { logger } from './utils/logger'

// Avoid "Uncaught (in promise)" when Vite HMR fails (e.g. app loaded via tunnel but HOST was unset, so client tries ws://localhost:64999).
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message ?? String(event.reason)
  if (
    typeof message === 'string' &&
    (message.includes('WebSocket closed without opened') ||
      message.includes('failed to connect to websocket'))
  ) {
    logger.debug(
      '[App] HMR WebSocket failed (expected when using tunnel without HOST set):',
      message,
    )
    event.preventDefault()
  }
})

/**
 * Initializes the app: i18n → store + client config (await) → Sentry + runtime config from that payload → then render.
 * Same pattern as `extensions/buyr-app-extension/blocks/src/index.tsx`: store hydrated before `<App />` mounts; ConfigGate only gates on error/missing config.
 * @throws Re-throws initialization errors after rendering an error UI
 */
export async function main() {
  try {
    logger.debug('[App] Starting initialization...')
    const { initI18n } = await import('./utils/i18n-utils')
    logger.debug('[App] Initializing i18n...')
    await initI18n()
    logger.debug('[App] i18n initialized, loading client config...')
    const { createStore } = await import('./store')
    const { fetchClientConfig } =
      await import('./store/thunks/client-config-thunks')
    const { initSentryFromConfig } = await import('./utils/sentry-init')
    const { setClientConfigRuntime } =
      await import('./utils/client-config-runtime')
    const store = createStore()
    await (store.dispatch as AppDispatch)(fetchClientConfig())
    const { config } = store.getState().clientConfig
    if (config) {
      setClientConfigRuntime(config as unknown as ClientConfigRuntime)
      try {
        initSentryFromConfig({ config })
      } catch (_err) {
        // Continue without Sentry
      }
    }
    logger.debug('[App] Client config loaded, loading App component...')
    const { default: App } = await import('./app')
    // Ensure that locales and config are loaded before rendering the app
    const rootElement = document.getElementById('app')
    if (!rootElement) {
      throw new Error('Root element id="app" not found')
    }
    logger.debug('[App] Rendering app...')
    const root = createRoot(rootElement)
    root.render(<App />)
    logger.debug('[App] App rendered successfully')
  } catch (error) {
    logger.error('[App] Failed to initialize:', error)
    const rootElement = document.getElementById('app')
    if (rootElement) {
      const root = createRoot(rootElement)
      root.render(
        <div style={{ padding: '20px', fontFamily: 'system-ui' }}>
          <h1>Application Error</h1>
          <p>Failed to initialize the application.</p>
          <pre
            style={{
              background: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {error instanceof Error ? error.message : String(error)}
            {error instanceof Error && error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </div>,
      )
    }
    throw error
  }
}

/** Max time to wait for window.shopify when embedded (avoids indefinite hang on reload race). */
const SHOPIFY_READY_TIMEOUT_MS = 5000
/** Interval for polling window.shopify when ready promise is not available. */
const SHOPIFY_POLL_INTERVAL_MS = 50

/**
 * Detects if the app is running inside the Shopify admin iframe (embedded context).
 * Uses URL params: host or embedded=1, which Shopify appends when loading the app in admin.
 */
function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.has('host') || params.get('embedded') === '1'
}

/**
 * Waits for window.shopify (App Bridge) to be ready. Uses shopify.ready promise when
 * available; otherwise polls. Resolves after timeout even if shopify never appears.
 */
function waitForShopify(): Promise<void> {
  return new Promise((resolve) => {
    const w = window as Window & { shopify?: { ready?: Promise<unknown> } }
    if (w.shopify) {
      if (
        typeof w.shopify.ready === 'object' &&
        w.shopify.ready instanceof Promise
      ) {
        w.shopify.ready.then(() => resolve()).catch(() => resolve())
        return
      }
      resolve()
      return
    }
    const deadline = Date.now() + SHOPIFY_READY_TIMEOUT_MS
    const poll = () => {
      if (w.shopify) {
        resolve()
        return
      }
      if (Date.now() >= deadline) {
        logger.warn(
          '[App] window.shopify not available after timeout; proceeding anyway',
        )
        resolve()
        return
      }
      setTimeout(poll, SHOPIFY_POLL_INTERVAL_MS)
    }
    poll()
  })
}

/**
 * Bootstrap entry point. When embedded, waits for App Bridge (window.shopify) before
 * initializing the app, avoiding the reload race where shopify is undefined.
 */
async function bootstrap(): Promise<void> {
  if (isEmbedded()) {
    const { persistEmbeddedAppParamsFromUrl } =
      await import('./utils/embedded-app-params')
    persistEmbeddedAppParamsFromUrl()
    await waitForShopify()
  }
  await main()
}

bootstrap().catch((error) => {
  logger.error('[App] Unhandled error in bootstrap():', error)
})
